// ─── src/GewLiveView.tsx ─────────────────────────────────────────────────────
//
// GEW Live — the week as it happens: events done / running / upcoming,
// door check-ins, who registered vs who came, and the events still missing
// check-ins (with the organiser's contact). Every number follows the filters
// (day, event, governorate, type, organiser). Refreshes every 2 minutes.
// "Attended" = checked in at the door, nothing else.

import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowLeft, CalendarCheck, CalendarClock, CheckCircle2, Download, MessageCircle,
  Radio, RefreshCw, Search, Ticket, UserCheck, Users, UsersRound, Venus, X,
} from "lucide-react";
import { getLiveSnapshot, type LiveEvent, type LiveFilters, type LivePhase } from "./api/gewLive";
import {
  ArrivalsChart, ChartCard, DaysChart, ProfileChart, RankBars, dayLabel, rankRows,
} from "./components/gewLive/LiveCharts";
import { TableSkeleton } from "./components/ui/Skeleton";

const PHASE: Record<LivePhase, { label: string; cls: string }> = {
  done: { label: "Done", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  running: { label: "Running now", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  upcoming: { label: "Upcoming", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" },
};

const fmt = (n: number) => n.toLocaleString("en-US");
const pct = (v: number | null) => (v == null ? "—" : `${v}%`);
const waLink = (phone: string) => `https://wa.me/${phone.replace(/[^\d]/g, "").replace(/^00/, "")}`;
const today = () => new Date().toISOString().slice(0, 10);

function downloadCsv(name: string, header: string[], rows: (string | number | null | undefined)[][]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GewLiveView() {
  const [filters, setFilters] = useState<LiveFilters>({});
  const set = (patch: Partial<LiveFilters>) =>
    setFilters((f) => {
      const next = { ...f, ...patch };
      (Object.keys(next) as (keyof LiveFilters)[]).forEach((k) => !next[k] && delete next[k]);
      return next;
    });

  const { data, isLoading, isError, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["gew-live", filters],
    queryFn: () => getLiveSnapshot(filters),
    refetchInterval: 120_000,
    placeholderData: keepPreviousData,
  });

  const selectedEvent = filters.event ? data?.events.find((e) => e.key === filters.event) : undefined;
  const k = data?.kpis;
  const hasCheckIns = (k?.attended ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-white text-xl font-bold flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-green" />
            </span>
            GEW Live
          </h2>
          <p className="text-brand-muted text-sm">
            GEW Syria 2026 as it happens. Attended = checked in at the door. Updates every 2 minutes.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-brand-muted">
          {dataUpdatedAt > 0 && (
            <span>
              Updated {new Date(dataUpdatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
              {data ? ` · Damascus ${data.now.time}` : ""}
            </span>
          )}
          <button onClick={() => refetch()} className="btn-ghost text-xs inline-flex items-center gap-1.5" disabled={isFetching}>
            <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Filters ──────────────────────────────────────────── */}
      <Filters data={data} filters={filters} set={set} clear={() => setFilters({})} />

      {isLoading && <TableSkeleton />}
      {isError && !data && <div className="card p-6 text-red-400 text-sm">Couldn&rsquo;t load GEW Live.</div>}

      {data && k && (
        <div className={`space-y-6 transition-opacity ${isFetching ? "opacity-70" : ""}`}>
          {selectedEvent && <EventHeader e={selectedEvent} onBack={() => set({ event: undefined })} />}

          {/* ── Event status ─────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <PhaseTile icon={CalendarCheck} label="Events done" value={k.eventsDone} total={k.eventsTotal} tone="slate" />
            <PhaseTile icon={Radio} label="Running now" value={k.eventsRunning} total={k.eventsTotal} tone="green" live={k.eventsRunning > 0} />
            <PhaseTile icon={CalendarClock} label="Upcoming" value={k.eventsUpcoming} total={k.eventsTotal} tone="sky" />
          </div>

          {/* ── People ───────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={UserCheck} label="Attended (checked in)" value={fmt(k.attended)} accent />
            <Stat icon={UsersRound} label="Unique attendees" value={fmt(k.uniqueAttendees)} hint="each person counted once" />
            <Stat icon={Users} label="Registered" value={fmt(k.registered)} hint={`${fmt(k.accepted)} accepted`} />
            <Stat icon={Ticket} label="Attended ÷ accepted" value={pct(k.showUpRate)} hint={`events that started (${k.startedEvents})`} />
            <Stat icon={Venus} label="Women among attendees" value={pct(k.femalePct)} hint="of those with gender given" />
            <Stat icon={CheckCircle2} label="Venue fill" value={pct(k.capacityFill)} hint="attended ÷ capacity, started events" />
            <Alert
              label="Started, no check-ins"
              value={k.noCheckIn}
              hint="in-person events with nobody scanned"
              tone="red"
              onClick={() => document.getElementById("no-checkin")?.scrollIntoView({ behavior: "smooth" })}
            />
            <Alert label="Check-ins under 10%" value={k.lowCheckIn} hint="ended events, of accepted" tone="amber" />
          </div>

          {/* ── Charts ───────────────────────────────────────── */}
          {!filters.event && !filters.day && (
            <ChartCard title="Registered vs attended by day" subtitle="Bars by the event's start day">
              <DaysChart days={data.days} />
            </ChartCard>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Arrivals by hour" subtitle="Door check-ins, Damascus time" empty={!hasCheckIns}>
              <ArrivalsChart arrivals={data.arrivals} />
            </ChartCard>
            <ChartCard title="Gender: registered vs attended" subtitle="Share of each group's total" height={200} empty={!hasCheckIns}>
              <ProfileChart rows={data.demographics.gender} />
            </ChartCard>
            <ChartCard title="Age group: registered vs attended" subtitle="Share of each group's total" empty={!hasCheckIns}>
              <ProfileChart rows={data.demographics.age} />
            </ChartCard>
            <ChartCard title="Status: registered vs attended" subtitle="Student, graduate, founder… share of each group's total" height={300} empty={!hasCheckIns}>
              <ProfileChart rows={data.demographics.status} />
            </ChartCard>
          </div>

          {!filters.event && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ChartCard title="Attended by governorate" height={Math.max(160, data.byGovernorate.length * 30)} empty={!hasCheckIns}>
                  <RankBars rows={data.byGovernorate.map((g) => ({ label: g.label, value: g.count }))} labelWidth={110} />
                </ChartCard>
                <ChartCard title="Attended by city (top 15)" height={Math.max(160, data.byCity.length * 30)} empty={!hasCheckIns}>
                  <RankBars rows={data.byCity.map((g) => ({ label: g.label, value: g.count }))} labelWidth={110} />
                </ChartCard>
                <ChartCard title="Top events by attendance" height={Math.max(160, data.topByAttendance.length * 34)} empty={!hasCheckIns}>
                  <RankBars rows={rankRows(data.topByAttendance, "attended")} />
                </ChartCard>
                <ChartCard
                  title="Top events by attended ÷ accepted"
                  subtitle="Events with at least 10 accepted"
                  height={Math.max(160, data.topByShowUp.length * 34)}
                  empty={!data.topByShowUp.length}
                >
                  <RankBars rows={rankRows(data.topByShowUp, "showUpRate")} unit="%" />
                </ChartCard>
              </div>
              <RepeatCard repeat={data.repeat} />
            </>
          )}

          {/* ── Events with no check-ins ─────────────────────── */}
          {!filters.event && <NoCheckInList rows={data.noCheckIn} />}

          {/* ── All events ───────────────────────────────────── */}
          {!filters.event && <EventsTable rows={data.events} onOpen={(e) => set({ event: e.key })} />}
        </div>
      )}
    </div>
  );
}

// ── Filters ───────────────────────────────────────────────────────────────────

function Filters({
  data, filters, set, clear,
}: {
  data?: Awaited<ReturnType<typeof getLiveSnapshot>>;
  filters: LiveFilters;
  set: (p: Partial<LiveFilters>) => void;
  clear: () => void;
}) {
  const days = data?.options.days ?? ["2026-11-01", "2026-11-02", "2026-11-03", "2026-11-04", "2026-11-05"];
  const events = (data?.options.events ?? []).filter((e) => !filters.day || e.eventDate === filters.day);
  const active = Object.keys(filters).length > 0;
  const sel = "bg-brand-navy border border-brand-border rounded-lg px-3 py-2 text-sm text-white max-w-full";
  return (
    <div className="card p-4 space-y-3">
      <div className="flex flex-wrap gap-2">
        <DayButton on={!filters.day} onClick={() => set({ day: undefined })}>All days</DayButton>
        {days.map((d) => (
          <DayButton key={d} on={filters.day === d} onClick={() => set({ day: d, event: undefined })}>
            {dayLabel(d)}
            {d === data?.now.date && <span className="ml-1.5 text-[10px] opacity-80">today</span>}
          </DayButton>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={filters.event ?? ""} onChange={(e) => set({ event: e.target.value || undefined })} className={`${sel} min-w-[260px]`} dir="auto">
          <option value="">All events ({events.length})</option>
          {events.map((e) => (
            <option key={e.key} value={e.key}>
              {e.eventDate.slice(8)}/11 {e.startTime ?? ""} · {e.title} — {e.organizer}
            </option>
          ))}
        </select>
        <select value={filters.governorate ?? ""} onChange={(e) => set({ governorate: e.target.value || undefined })} className={sel} dir="auto">
          <option value="">All governorates</option>
          {data?.options.governorates.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={filters.type ?? ""} onChange={(e) => set({ type: (e.target.value || undefined) as LiveFilters["type"] })} className={sel}>
          <option value="">Partner events + meetups</option>
          <option value="gew_event">Partner events</option>
          <option value="meetup">Catalyst meetups</option>
        </select>
        <select value={filters.organizer ?? ""} onChange={(e) => set({ organizer: e.target.value || undefined })} className={`${sel} min-w-[200px]`} dir="auto">
          <option value="">All organisers</option>
          {data?.options.organizers.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {active && (
          <button onClick={clear} className="btn-ghost text-sm inline-flex items-center gap-1.5">
            <X size={13} /> Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

function DayButton({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3.5 py-1.5 rounded-lg text-sm border transition-colors ${
        on ? "bg-brand-green text-gray-900 border-brand-green font-semibold" : "border-brand-border text-brand-muted hover:text-white hover:bg-brand-border/40"
      }`}
    >
      {children}
    </button>
  );
}

// ── Tiles ─────────────────────────────────────────────────────────────────────

const TONE = {
  slate: { ring: "border-slate-500/30", icon: "bg-slate-500/20 text-slate-200", bar: "bg-slate-400" },
  green: { ring: "border-emerald-500/40", icon: "bg-emerald-500/20 text-emerald-300", bar: "bg-emerald-400" },
  sky: { ring: "border-sky-500/30", icon: "bg-sky-500/15 text-sky-300", bar: "bg-sky-400" },
} as const;

function PhaseTile({
  icon: Icon, label, value, total, tone, live,
}: {
  icon: React.ElementType; label: string; value: number; total: number; tone: keyof typeof TONE; live?: boolean;
}) {
  const t = TONE[tone];
  const share = total ? (value / total) * 100 : 0;
  return (
    <div className={`card p-5 border ${t.ring}`}>
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.icon}`}>
          <Icon size={20} />
        </div>
        {live && <span className="text-[11px] font-semibold text-emerald-300 uppercase tracking-wider">● Live</span>}
      </div>
      <p className="text-brand-muted text-sm mt-4">{label}</p>
      <p className="text-white text-3xl font-bold mt-0.5">
        {fmt(value)} <span className="text-brand-muted text-base font-normal">of {fmt(total)}</span>
      </p>
      <div className="mt-3 h-1.5 rounded-full bg-brand-border overflow-hidden">
        <div className={`h-full rounded-full ${t.bar}`} style={{ width: `${share}%` }} />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, accent }: { icon: React.ElementType; label: string; value: string; hint?: string; accent?: boolean }) {
  return (
    <div className={`card p-4 ${accent ? "border border-emerald-500/40" : ""}`}>
      <div className="flex items-center gap-2 text-brand-muted text-xs">
        <Icon size={14} className={accent ? "text-emerald-300" : ""} /> {label}
      </div>
      <p className="text-white text-2xl font-bold mt-1.5">{value}</p>
      {hint && <p className="text-brand-muted text-[11px] mt-0.5">{hint}</p>}
    </div>
  );
}

function Alert({ label, value, hint, tone, onClick }: { label: string; value: number; hint: string; tone: "red" | "amber"; onClick?: () => void }) {
  const on = value > 0;
  const cls = !on
    ? "border-brand-border"
    : tone === "red"
      ? "border-red-500/50 bg-red-500/5"
      : "border-amber-500/50 bg-amber-500/5";
  const txt = !on ? "text-white" : tone === "red" ? "text-red-300" : "text-amber-300";
  return (
    <button onClick={onClick} disabled={!onClick || !on} className={`card p-4 border text-left ${cls} ${onClick && on ? "hover:brightness-125 cursor-pointer" : "cursor-default"}`}>
      <div className={`flex items-center gap-2 text-xs ${on ? txt : "text-brand-muted"}`}>
        <AlertTriangle size={14} /> {label}
      </div>
      <p className={`text-2xl font-bold mt-1.5 ${txt}`}>{value}</p>
      <p className="text-brand-muted text-[11px] mt-0.5">{hint}</p>
    </button>
  );
}

function RepeatCard({ repeat }: { repeat: { one: number; two: number; threePlus: number } }) {
  const total = repeat.one + repeat.two + repeat.threePlus;
  const rows = [
    { label: "1 event", value: repeat.one },
    { label: "2 events", value: repeat.two },
    { label: "3+ events", value: repeat.threePlus },
  ];
  return (
    <div className="card p-5">
      <p className="text-white font-semibold text-sm">How many events each attendee came to</p>
      <p className="text-brand-muted text-xs mt-0.5">Unique attendees by number of events checked in to</p>
      <div className="grid grid-cols-3 gap-3 mt-4">
        {rows.map((r) => (
          <div key={r.label} className="rounded-xl border border-brand-border p-4">
            <p className="text-brand-muted text-xs">{r.label}</p>
            <p className="text-white text-2xl font-bold mt-1">{fmt(r.value)}</p>
            <div className="mt-2 h-1.5 rounded-full bg-brand-border overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${total ? (r.value / total) * 100 : 0}%`, background: "#0FA57A" }} />
            </div>
            <p className="text-brand-muted text-[11px] mt-1">{total ? Math.round((r.value / total) * 100) : 0}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Single event header ───────────────────────────────────────────────────────

function EventHeader({ e, onBack }: { e: LiveEvent; onBack: () => void }) {
  return (
    <div className="card p-5 space-y-3">
      <button onClick={onBack} className="btn-ghost text-xs inline-flex items-center gap-1.5">
        <ArrowLeft size={13} /> All events
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div dir="rtl" className="min-w-0">
          <p className="text-white text-lg font-bold">{e.title}</p>
          <p className="text-brand-muted text-sm">
            {e.organizer} · {e.city ?? e.governorate} · {e.eventDate} {e.startTime ?? ""}
            {e.endTime ? `–${e.endTime}` : ""} · {e.sourceType === "meetup" ? "ميتاب كتالست" : "فعالية شريك"}
            {e.mode === "online" ? " · أونلاين" : ""}
          </p>
        </div>
        <PhaseBadge phase={e.phase} />
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-brand-muted">
        <span>Capacity: <span className="text-white">{e.capacity ?? "—"}</span></span>
        <span>Feedback: <span className="text-white">{e.feedback.responses}</span> answers of {e.feedback.emailed} emailed</span>
        {e.contact.phone && (
          <a href={waLink(e.contact.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:underline">
            <MessageCircle size={13} /> {e.contact.name ?? "Organiser"} · <span dir="ltr">{e.contact.phone}</span>
          </a>
        )}
      </div>
      {(e.noCheckIn || e.lowCheckIn) && <Flags e={e} />}
    </div>
  );
}

function PhaseBadge({ phase }: { phase: LivePhase }) {
  const p = PHASE[phase];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border whitespace-nowrap ${p.cls}`}>{p.label}</span>;
}

function Flags({ e }: { e: LiveEvent }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {e.noCheckIn && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-red-500/40 bg-red-500/10 text-red-300 whitespace-nowrap">
          <AlertTriangle size={11} /> No check-ins
        </span>
      )}
      {e.lowCheckIn && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-amber-500/40 bg-amber-500/10 text-amber-300 whitespace-nowrap">
          <AlertTriangle size={11} /> Under 10%
        </span>
      )}
    </span>
  );
}

// ── No check-in list ──────────────────────────────────────────────────────────

function NoCheckInList({ rows }: { rows: LiveEvent[] }) {
  return (
    <div id="no-checkin" className="card overflow-hidden scroll-mt-24">
      <div className="px-5 py-4 border-b border-brand-border flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-white font-semibold text-sm flex items-center gap-2">
            <AlertTriangle size={15} className={rows.length ? "text-red-300" : "text-brand-muted"} />
            Started with no check-ins ({rows.length})
          </p>
          <p className="text-brand-muted text-xs mt-0.5">
            In-person events that started and have nobody scanned. Their attendees won&rsquo;t get the feedback form.
          </p>
        </div>
        {rows.length > 0 && (
          <button
            className="btn-ghost text-sm inline-flex items-center gap-1.5"
            onClick={() =>
              downloadCsv(
                `gew_no_checkin_${today()}.csv`,
                ["Date", "Start", "Event", "Organiser", "City", "Registered", "Accepted", "Contact", "Phone", "Email"],
                rows.map((r) => [r.eventDate, r.startTime, r.title, r.organizer, r.city ?? r.governorate, r.registered, r.accepted, r.contact.name, r.contact.phone, r.contact.email]),
              )
            }
          >
            <Download size={14} /> CSV
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-brand-muted text-sm">Every started in-person event has check-ins. 🎉</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border text-brand-muted">
                <th className="px-4 py-3 text-left font-medium">Start</th>
                <th className="px-4 py-3 text-left font-medium">Event</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Registered</th>
                <th className="px-4 py-3 text-left font-medium">Contact</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-brand-border/50">
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{dayLabel(r.eventDate)} · {r.startTime ?? "—"}</td>
                  <td className="px-4 py-3 min-w-[240px]" dir="rtl">
                    <p className="text-white">{r.title}</p>
                    <p className="text-brand-muted text-xs">{r.organizer} · {r.city ?? r.governorate}</p>
                  </td>
                  <td className="px-4 py-3"><PhaseBadge phase={r.phase} /></td>
                  <td className="px-4 py-3 text-white">{fmt(r.registered)} <span className="text-brand-muted text-xs">({fmt(r.accepted)} accepted)</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p className="text-white" dir="auto">{r.contact.name ?? "—"}</p>
                    {r.contact.phone ? (
                      <a href={waLink(r.contact.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 text-xs hover:underline">
                        <MessageCircle size={12} /> <span dir="ltr">{r.contact.phone}</span>
                      </a>
                    ) : (
                      <p className="text-brand-muted text-xs">{r.contact.email ?? "no phone"}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Events table ──────────────────────────────────────────────────────────────

type SortKey = "start" | "title" | "registered" | "accepted" | "attended" | "showUpRate" | "femalePct" | "capacityFill" | "feedback";
const COLS: [SortKey, string][] = [
  ["start", "Date"],
  ["title", "Event"],
  ["registered", "Registered"],
  ["accepted", "Accepted"],
  ["attended", "Attended"],
  ["showUpRate", "Att. ÷ acc."],
  ["femalePct", "Women"],
  ["capacityFill", "Venue fill"],
  ["feedback", "Feedback"],
];

function EventsTable({ rows, onOpen }: { rows: LiveEvent[]; onOpen: (e: LiveEvent) => void }) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "start", dir: 1 });
  const [q, setQ] = useState("");
  const value = (r: LiveEvent, k: SortKey): string | number | null =>
    k === "start" ? `${r.eventDate} ${r.startTime ?? ""}` : k === "title" ? r.title : k === "feedback" ? r.feedback.responses : r[k];
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows
      .filter((r) => !s || `${r.title} ${r.organizer} ${r.city ?? ""}`.toLowerCase().includes(s))
      .sort((a, b) => {
        const x = value(a, sort.key);
        const y = value(b, sort.key);
        if (x == null && y == null) return 0;
        if (x == null) return 1;
        if (y == null) return -1;
        return (typeof x === "number" ? x - (y as number) : String(x).localeCompare(String(y))) * sort.dir;
      });
  }, [rows, q, sort]);
  const toggle = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : key === "start" || key === "title" ? 1 : -1 }));

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-brand-border flex flex-wrap items-center gap-3">
        <p className="text-white font-semibold text-sm mr-auto">Events ({shown.length})</p>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-brand-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Event, organiser, city…"
            className="bg-brand-navy border border-brand-border rounded-lg pl-8 pr-3 py-1.5 text-sm text-white"
          />
        </div>
        <button
          className="btn-ghost text-sm inline-flex items-center gap-1.5"
          onClick={() =>
            downloadCsv(
              `gew_live_events_${today()}.csv`,
              ["Date", "Start", "End", "Status", "Event", "Type", "Organiser", "Governorate", "City", "Mode", "Capacity", "Registered", "Accepted", "Attended", "Attended/accepted %", "Women %", "Venue fill %", "Feedback emailed", "Feedback answers", "No check-ins", "Under 10%", "Contact", "Phone"],
              shown.map((r) => [
                r.eventDate, r.startTime, r.endTime, PHASE[r.phase].label, r.title, r.sourceType === "meetup" ? "Catalyst meetup" : "Partner event",
                r.organizer, r.governorate, r.city, r.mode, r.capacity, r.registered, r.accepted, r.attended, r.showUpRate, r.femalePct,
                r.capacityFill, r.feedback.emailed, r.feedback.responses, r.noCheckIn ? "Yes" : "", r.lowCheckIn ? "Yes" : "", r.contact.name, r.contact.phone,
              ]),
            )
          }
        >
          <Download size={14} /> CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border">
              {COLS.map(([key, label]) => (
                <th key={key} className="px-4 py-3 text-left text-brand-muted font-medium whitespace-nowrap">
                  <button onClick={() => toggle(key)} className="hover:text-white">
                    {label}
                    {sort.key === key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.key} onClick={() => onOpen(r)} className="border-b border-brand-border/50 hover:bg-brand-border/20 cursor-pointer transition-colors">
                <td className="px-4 py-3 whitespace-nowrap">
                  <p className="text-white">{dayLabel(r.eventDate)}</p>
                  <p className="text-brand-muted text-xs">{r.startTime ?? "—"}</p>
                  <div className="mt-1"><PhaseBadge phase={r.phase} /></div>
                </td>
                <td className="px-4 py-3 min-w-[260px]" dir="rtl">
                  <p className="text-white font-medium">{r.title}</p>
                  <p className="text-brand-muted text-xs">
                    {r.organizer} · {r.city ?? r.governorate}
                    {r.sourceType === "meetup" ? " · ميتاب" : ""}
                    {r.mode === "online" ? " · أونلاين" : ""}
                  </p>
                  {(r.noCheckIn || r.lowCheckIn) && <div className="mt-1"><Flags e={r} /></div>}
                </td>
                <td className="px-4 py-3 text-brand-muted">{fmt(r.registered)}</td>
                <td className="px-4 py-3 text-brand-muted">{fmt(r.accepted)}</td>
                <td className="px-4 py-3 text-white font-semibold">
                  {r.phase === "upcoming" && !r.attended ? "—" : fmt(r.attended)}
                </td>
                <td className="px-4 py-3 text-white">{r.phase === "upcoming" && !r.attended ? "—" : pct(r.showUpRate)}</td>
                <td className="px-4 py-3 text-brand-muted">{pct(r.femalePct)}</td>
                <td className="px-4 py-3 text-brand-muted">{r.phase === "upcoming" ? "—" : pct(r.capacityFill)}</td>
                <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                  {r.feedback.emailed ? `${r.feedback.responses} / ${r.feedback.emailed}` : "—"}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={COLS.length} className="px-4 py-10 text-center text-brand-muted">No events match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
